import { CardElement, ColorRank } from "../types/battle"
import {
  CardShardSlot,
  CraftShardResponse,
  InstalledShard,
  PlayerShard,
  ShardCatalogEntry,
  ShardDustByElement,
  ShardDustConfig,
  ShardEffect,
  ShardTier,
  ShardsCatalogResponse,
  ShardsInventoryResponse,
  CrystallizedResult,
  DustGrant,
} from "../types/shards"
import { AFFILIATION_ELEMENTS } from "../constants/affiliationInfo"
import { SHARD_SLOTS_BY_RANK } from "../constants/shards"

type RawRecord = Record<string, unknown>

const CARD_ELEMENTS: CardElement[] = [
  "FIRE",
  "WATER",
  "EARTH",
  "LIGHTNING",
  "WIND",
  "ICE",
]

const COLOR_RANKS: ColorRank[] = [
  "WHITE",
  "GREEN",
  "BLUE",
  "PURPLE",
  "ORANGE",
  "RED",
]

const SHARD_TIERS: ShardTier[] = ["COMMON", "RARE", "EPIC"]

function pickString(value: unknown, fallback = ""): string {
  if (typeof value === "string") return value
  if (value != null) return String(value)
  return fallback
}

function pickNumber(value: unknown, fallback = 0): number {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string" && value.trim()) {
    const n = Number(value)
    return Number.isFinite(n) ? n : fallback
  }
  return fallback
}

function pickEnum<T extends string>(
  value: unknown,
  allowed: readonly T[],
  fallback: T
): T {
  const raw = pickString(value).toUpperCase()
  return allowed.includes(raw as T) ? (raw as T) : fallback
}

function unwrapPayload(data: unknown): RawRecord {
  if (!data || typeof data !== "object" || Array.isArray(data)) return {}
  const obj = data as RawRecord
  const nested = obj.data ?? obj.result ?? obj.payload
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    return { ...(nested as RawRecord), ...obj }
  }
  return obj
}

export function defaultShardDust(): ShardDustByElement {
  return {
    FIRE: 0,
    WATER: 0,
    EARTH: 0,
    LIGHTNING: 0,
    WIND: 0,
    ICE: 0,
  }
}

function normalizeShardEffect(raw: unknown): ShardEffect | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as RawRecord
  const type = pickString(obj.type)
  if (!type) return null
  return { type, value: pickNumber(obj.value) }
}

function normalizeShardDefinition(raw: unknown): PlayerShard["definition"] {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return { title: "", description: "", effects: [] }
  }
  const obj = raw as RawRecord
  const effectsRaw = obj.effects
  const effects = Array.isArray(effectsRaw)
    ? effectsRaw
        .map(normalizeShardEffect)
        .filter((item): item is ShardEffect => item != null)
    : []
  return {
    key: pickString(obj.key) || undefined,
    title: pickString(obj.title),
    description: pickString(obj.description ?? obj.desc),
    effects,
    tierLabel: pickString(obj.tierLabel ?? obj.tier_label) || undefined,
    elementLabel: pickString(obj.elementLabel ?? obj.element_label) || undefined,
  }
}

export function normalizeShardCatalogEntry(raw: unknown): ShardCatalogEntry | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as RawRecord
  const key = pickString(obj.key)
  if (!key) return null
  const effectsRaw = obj.effects
  const effects = Array.isArray(effectsRaw)
    ? effectsRaw
        .map(normalizeShardEffect)
        .filter((item): item is ShardEffect => item != null)
    : []
  return {
    key,
    element: pickEnum(obj.element, CARD_ELEMENTS, "FIRE"),
    tier: pickEnum(obj.tier, SHARD_TIERS, "COMMON"),
    title: pickString(obj.title),
    description: pickString(obj.description ?? obj.desc),
    effects,
    colorRank: pickEnum(obj.colorRank ?? obj.color_rank, COLOR_RANKS, "WHITE"),
    craftCost: pickNumber(obj.craftCost ?? obj.craft_cost),
    tierLabel: pickString(obj.tierLabel ?? obj.tier_label) || undefined,
    elementLabel: pickString(obj.elementLabel ?? obj.element_label) || undefined,
  }
}

function normalizeRankNumberMap(raw: unknown): Partial<Record<ColorRank, number>> {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {}
  const result: Partial<Record<ColorRank, number>> = {}
  for (const rank of COLOR_RANKS) {
    const value = (raw as RawRecord)[rank]
    if (value != null) result[rank] = pickNumber(value)
  }
  return result
}

function normalizeSlotsByRank(raw: unknown): Partial<Record<ColorRank, number>> {
  const fromApi = normalizeRankNumberMap(raw)
  if (Object.keys(fromApi).length > 0) return fromApi
  return { ...SHARD_SLOTS_BY_RANK }
}

export function normalizeShardDust(raw: unknown): ShardDustByElement {
  const base = defaultShardDust()
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return base
  const obj = raw as RawRecord
  for (const element of AFFILIATION_ELEMENTS) {
    const value = obj[element] ?? obj[element.toLowerCase()]
    if (value != null) base[element] = pickNumber(value)
  }
  return base
}

function normalizeDustConfig(raw: unknown): ShardDustConfig {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return {
      dailyBattleCap: 120,
      fuseFailDust: {},
      physicalCardDust: {},
    }
  }
  const obj = raw as RawRecord
  return {
    dailyBattleCap: pickNumber(
      obj.dailyBattleCap ?? obj.daily_battle_cap,
      120
    ),
    fuseFailDust: normalizeRankNumberMap(
      obj.fuseFailDust ?? obj.fuse_fail_dust
    ),
    physicalCardDust: normalizeRankNumberMap(
      obj.physicalCardDust ?? obj.physical_card_dust
    ),
  }
}

export function normalizeShardsCatalog(data: unknown): ShardsCatalogResponse {
  const obj = unwrapPayload(data)
  const shardsRaw = obj.shards ?? obj.catalog
  const shards = Array.isArray(shardsRaw)
    ? shardsRaw
        .map(normalizeShardCatalogEntry)
        .filter((item): item is ShardCatalogEntry => item != null)
    : []
  return {
    shards,
    dustConfig: normalizeDustConfig(obj.dustConfig ?? obj.dust_config),
    slotsByRank: normalizeSlotsByRank(obj.slotsByRank ?? obj.slots_by_rank),
  }
}

function resolveShardElement(obj: RawRecord): CardElement | null {
  const direct = pickString(obj.element).toUpperCase()
  if (CARD_ELEMENTS.includes(direct as CardElement)) {
    return direct as CardElement
  }
  const nested =
    obj.definition &&
    typeof obj.definition === "object" &&
    !Array.isArray(obj.definition)
      ? (obj.definition as RawRecord)
      : null
  const fromDef = pickString(nested?.element).toUpperCase()
  if (CARD_ELEMENTS.includes(fromDef as CardElement)) {
    return fromDef as CardElement
  }
  return null
}

/** Установленный осколок; публичный/slim DTO может быть без id. */
export function normalizeInstalledShard(raw: unknown): InstalledShard | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as RawRecord
  const element = resolveShardElement(obj)
  if (!element) return null
  const tier = pickEnum(obj.tier, SHARD_TIERS, "COMMON")
  const id = obj.id ?? obj.uuid ?? obj.shardId ?? obj.shard_id
  return {
    id: id != null ? String(id) : `display:${element}:${tier}`,
    element,
    tier,
    colorRank:
      pickEnum(obj.colorRank ?? obj.color_rank, COLOR_RANKS, "WHITE") ||
      undefined,
    definition: normalizeShardDefinition(obj.definition ?? obj),
  }
}

export function normalizePlayerShard(raw: unknown): PlayerShard | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as RawRecord
  const id = obj.id ?? obj.uuid
  if (id == null) return null
  return {
    id: String(id),
    element: pickEnum(obj.element, CARD_ELEMENTS, "FIRE"),
    colorRank: pickEnum(obj.colorRank ?? obj.color_rank, COLOR_RANKS, "WHITE"),
    tier: pickEnum(obj.tier, SHARD_TIERS, "COMMON"),
    definition: normalizeShardDefinition(obj.definition ?? obj),
    craftCost: pickNumber(obj.craftCost ?? obj.craft_cost) || undefined,
  }
}

export function normalizeShardsInventory(data: unknown): ShardsInventoryResponse {
  const obj = unwrapPayload(data)
  const uninstalledRaw =
    obj.uninstalledShards ?? obj.uninstalled_shards ?? obj.shards
  const uninstalledShards = Array.isArray(uninstalledRaw)
    ? uninstalledRaw
        .map(normalizePlayerShard)
        .filter((item): item is PlayerShard => item != null)
    : []
  return {
    dust: normalizeShardDust(obj.dust ?? obj.shardDust ?? obj.shard_dust),
    battlesDustToday: pickNumber(
      obj.battlesDustToday ?? obj.battles_dust_today
    ),
    dailyBattleCap: pickNumber(
      obj.dailyBattleCap ?? obj.daily_battle_cap,
      120
    ),
    uninstalledShards,
  }
}

export function normalizeCraftShardResponse(data: unknown): CraftShardResponse {
  const obj = unwrapPayload(data)
  const shard =
    normalizePlayerShard(obj.shard) ??
    normalizePlayerShard(obj.createdShard ?? obj.created_shard)
  if (!shard) {
    throw new Error("Некорректный ответ сервера после крафта")
  }
  return {
    shard,
    spentDust: pickNumber(obj.spentDust ?? obj.spent_dust),
    remainingDust: pickNumber(obj.remainingDust ?? obj.remaining_dust),
  }
}

export function normalizeCardShardSlot(raw: unknown): CardShardSlot | null {
  // Элемент строкой: ["FIRE", "WATER"]
  if (typeof raw === "string") {
    const element = raw.toUpperCase()
    if (!CARD_ELEMENTS.includes(element as CardElement)) return null
    return {
      slotIndex: 0,
      shard: normalizeInstalledShard({ element }),
    }
  }
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as RawRecord
  const slotIndex = pickNumber(
    obj.slotIndex ?? obj.slot_index ?? obj.index ?? obj.slot
  )
  const shardRaw =
    obj.shard ??
    obj.installedShard ??
    obj.installed_shard ??
    obj.installed ??
    obj.shardInfo ??
    obj.shard_info
  let shard: InstalledShard | null = null
  if (typeof shardRaw === "string") {
    shard = normalizeInstalledShard({ element: shardRaw })
  } else if (shardRaw != null && typeof shardRaw === "object") {
    shard = normalizeInstalledShard(shardRaw)
  }
  // Публичный профиль / slim DTO: осколок лежит плоско в слоте
  if (!shard) {
    shard = normalizeInstalledShard(obj)
  }
  // Пустой слот (есть index, нет осколка) — сохраняем для UI слотов
  if (!shard && shardRaw === null) {
    return { slotIndex, shard: null }
  }
  return { slotIndex, shard }
}

/** shardSlots / installedShards / плоский массив осколков с API. */
export function pickCardShardSlotsRaw(
  obj: Record<string, unknown>
): unknown {
  return (
    obj.shardSlots ??
    obj.shard_slots ??
    obj.installedShards ??
    obj.installed_shards ??
    obj.equippedShards ??
    obj.equipped_shards ??
    obj.cardShards ??
    obj.card_shards ??
    obj.shards
  )
}

export function normalizeCardShardSlots(raw: unknown): CardShardSlot[] {
  if (!Array.isArray(raw)) return []
  return raw
    .map((item, index) => {
      const slot = normalizeCardShardSlot(item)
      if (!slot) return null
      // Плоский список без slotIndex — по порядку в массиве
      const hasExplicitIndex =
        item &&
        typeof item === "object" &&
        !Array.isArray(item) &&
        ((item as RawRecord).slotIndex != null ||
          (item as RawRecord).slot_index != null ||
          (item as RawRecord).index != null ||
          (item as RawRecord).slot != null)
      if (!hasExplicitIndex) {
        return { ...slot, slotIndex: index }
      }
      return slot
    })
    .filter((item): item is CardShardSlot => item != null)
    .sort((a, b) => a.slotIndex - b.slotIndex)
}

export function parseDustGrant(raw: unknown): DustGrant | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as RawRecord
  const dustGranted = pickNumber(obj.dustGranted ?? obj.dust_granted)
  if (dustGranted <= 0) return null
  const elementRaw = pickString(obj.dustElement ?? obj.dust_element).toUpperCase()
  const dustElement = CARD_ELEMENTS.includes(elementRaw as CardElement)
    ? (elementRaw as CardElement)
    : null
  return { dustGranted, dustElement }
}

export function normalizeCrystallized(raw: unknown): CrystallizedResult | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as RawRecord
  const basePowerGain = pickNumber(
    obj.basePowerGain ?? obj.base_power_gain
  )
  const refundRaw = obj.dustRefund ?? obj.dust_refund
  const dustRefund: Partial<ShardDustByElement> = {}
  if (refundRaw && typeof refundRaw === "object" && !Array.isArray(refundRaw)) {
    for (const element of AFFILIATION_ELEMENTS) {
      const value = (refundRaw as RawRecord)[element]
      if (value != null) dustRefund[element] = pickNumber(value)
    }
  }
  if (basePowerGain <= 0 && Object.keys(dustRefund).length === 0) return null
  return { basePowerGain, dustRefund }
}

export function formatDustGrantMessage(grant: DustGrant): string {
  const elementLabel = grant.dustElement ?? "стихии"
  return `+${grant.dustGranted} пыли (${elementLabel})`
}

export function formatCrystallizedMessage(result: CrystallizedResult): string {
  const refundTotal = Object.values(result.dustRefund).reduce(
    (sum, n) => sum + (n ?? 0),
    0
  )
  const parts = [
    "Осколки кристаллизованы в силу карты. Слоты очищены.",
  ]
  if (result.basePowerGain > 0) {
    parts.push(`+${result.basePowerGain} к базовой силе.`)
  }
  if (refundTotal > 0) {
    parts.push(`Возвращено ${refundTotal} пыли.`)
  }
  return parts.join(" ")
}
